import { RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentInfos, CoreComponentStyle } from '@/core/models/app-components/types'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompLocationProps } from './comp'
import { CompLocationSettings } from './types'

export interface CompLocationWrapperProps {
  infos: CoreComponentInfos
  style: CoreComponentStyle
  children: (props: CompLocationProps) => React.ReactNode
}

/**
 * CompLocationWrapperCore
 */
export const CompLocationWrapperCore = ({
  infos: { settings, containerClient, detailTableId, detailRowIndex, component },
  style: { mode },
  children,
}: CompLocationWrapperProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { simulateAction } = useBuilderActionContextV2()

  const address: string =
    useParseVariableSelect(
      settings[ComponentSettingKey.LocationDataAddress],
      containerClient,
      detailTableId,
      detailRowIndex
    ) ?? ''
  const arrAddress = address.split(',')
  const latitude = arrAddress && +address?.split(',')[0]
  const longitude = arrAddress && +address?.split(',')[1]

  const icon: string =
    useParseVariableSelect(
      settings[ComponentSettingKey.LocationDataMarker],
      containerClient,
      detailTableId,
      detailRowIndex
    ) ?? ''

  const title = useParseVariableText(
    settings[ComponentSettingKey.LocationDataHeader],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const subtitle = useParseVariableText(
    settings[ComponentSettingKey.LocationDataSubHeader],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const description = useParseVariableText(
    settings[ComponentSettingKey.LocationDataDescription],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const avatar: string =
    useParseVariableSelect(
      settings[ComponentSettingKey.LocationDataAvatar],
      containerClient,
      detailTableId,
      detailRowIndex
    ) ?? ''

  const image: string =
    useParseVariableSelect(
      settings[ComponentSettingKey.LocationDataImage],
      containerClient,
      detailTableId,
      detailRowIndex
    ) ?? ''

  const styles = {}

  const draggable = getSafely(settings, [ComponentSettingKey.LocationBehaviorDraggable], true)
  const mapTypeControl = getSafely(settings, [ComponentSettingKey.LocationBehaviorMapTypeControl], true)
  const fullscreenControl = getSafely(settings, [ComponentSettingKey.LocationBehaviorFullscreenControl], true)
  const streetViewControl = getSafely(settings, [ComponentSettingKey.LocationBehaviorStreetViewControl], true)
  const zoomControl = getSafely(settings, [ComponentSettingKey.LocationBehaviorZoomControl], true)
  const zoomLevel = getSafely(settings, [ComponentSettingKey.LocationBehaviorZoomLevel], 'far')
  const infoBubble = getSafely(settings, [ComponentSettingKey.LocationBehaviorInfoBubble], 'default')
  const clickEvents = getSafely(settings, [ComponentSettingKey.LocationActionOnClick], [])
  const width = getSafely(settings, [ComponentSettingKey.LocationDesignWidth], [100])
  const height = getSafely(settings, [ComponentSettingKey.LocationDesignHeight], [900])
  const alignment = getSafely(settings, [ComponentSettingKey.LocationDesignAlignment], 'center')

  const shape = settings?.[ComponentSettingKey.LocationDesignShape]
  const margin = settings?.[ComponentSettingKey.LocationDesignMargin] as RawSpacingInput
  const colorVariant = getSafely(settings, [ComponentSettingKey.LocationDesignScheme], 'primary-90')
  const colorMarker = getSafely(settings, [ComponentSettingKey.LocationDesignColorMarker], 'primary-90')
  const compSettings: CompLocationSettings = {
    tableId: settings._id,
    rowId: settings._id,
    rowIndex: settings._id,
    latitude,
    longitude,
    avatar,
    description,
    draggable,
    fullscreenControl,
    title,
    subtitle,
    icon,
    image,
    mapTypeControl,
    streetViewControl,
    zoomControl,
    zoomLevel,
    info: infoBubble,
    width: width[0],
    height: height[0],
    alignment,
    theme: mode,
    shape,
    margin,
    clickEvents,
    colorVariant,
    colorMarker,
    simulateAction,
  }

  if (!latitude || !longitude) return null

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const locationComponent = children({ settings: compSettings })

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {locationComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return locationComponent
}
